// redux imports
import store from 'store'
// module imports
import { isPhoneNumber } from 'modules/PhoneNumbers'
// constant imports
import { SERVER_VERSION } from 'constants/Servers'
import SESSIONS from 'constants/Sessions'

/**
 * Decodes a Base64 URL.
 *
 * @param {string} base64Url
 * @returns {string}
 */
export const decodeB64Url = (base64Url) => {
  const str = base64Url.replace(/-/g, '+').replace(/_/g, '/')
  const padding = '='.repeat((4 - (str.length % 4)) % 4)

  return window.atob(str + padding)
}

/**
 * decodes a string which has been encoded using Base64 encoding
 *
 * @param {string} encodedString
 * @param {number} [index=1]
 * @returns {string}
 */
export const decodeStringEncodedBase64 = (encodedString, index = 1) => {
  return JSON.parse(decodeB64Url(encodedString.split('.')[index]))
}

/**
 * Gets the initials from a string.
 *
 * @param {string} name
 * @returns {string}
 */
export const initialsFromString = (name = '') => {
  // validate input
  if (!name || isPhoneNumber(name) || name === SESSIONS.hidden.restricted) {
    return ''
  }

  // name is only one letter
  if (name.length === 1) return name

  const names = name.trim().split(' ')

  // name only has one word
  if (names.length === 1) {
    return name.substr(0, 1)
  }

  // name has multiple words
  return name[0] + (names[names.length - 1][0] || '')
}

/**
 * Converts camelCase to snake_case
 *
 * @param {string} camelCase string to convert
 * @returns {string} snake_case string
 */
export const camel2Snake = (camelCase) =>
  (camelCase || '')
    .replace(/[\w]([A-Z])/g, (m) => `${m[0]}_${m[1]}`)
    .toLowerCase()

/**
 * Is click inside of ref element
 *
 * @param ref
 * @param event
 * @returns {boolean} if click is inside
 */
export const isClickInside = (ref, event) =>
  ref.current && ref.current.contains(event.target)

/**
 * Converts sentence to Title Case
 *
 * @param {string} sentence string to convert
 * @returns {string} Title Case String
 */
export const titleCase = (sentence) => {
  if (!sentence?.split) return ''

  const titleize = (word) =>
    word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()

  return sentence
    .split(' ')
    .map((word) => titleize(word))
    .join(' ')
}

/**
 * Converts semicolon delimited string to object
 *
 * @param {string} data string to convert to object
 * @returns {{Object.<string, string>}} object with key value mapping
 */
export const semicolonDelimited2Object = (data = '') => {
  const obj = Object.fromEntries(
    data
      .split(';')
      .map((item) => item.replace(/"/g, '').toLowerCase().split('=')),
  )
  return Object.keys(obj).length ? obj : undefined
}

/**
 * Is call server 3
 * @deprecated the primary server is version 3 as there is no more development for version 2 and planning when possible to get rid of version 2
 * @returns {boolean} is call server 3
 */
export const isCSV3 = () =>
  store.getState().config.version === SERVER_VERSION.V3

/**
 * Is call server 2
 *
 * @returns {boolean} is call server 2
 */
export const isCSV2 = () =>
  store.getState().config.version === SERVER_VERSION.V2

/**
 * Identify if string is an svg
 *
 * @param {string} str to identify
 * @returns {boolean} is the string an svg string
 */
export const isSvgString = (str) => (str || '').startsWith('<svg')

export const escapedRegExp = (str, options) =>
  new RegExp((str || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), options)

/**
 * Translate svg string into base 64 string to use as <img> src
 *
 * @param {string} svg
 * @returns {string}
 */
export const svgImageSource = (svg) =>
  `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(svg)))}`

/**
 * Check if value exists
 * Returns true for 1, true, false, '', [], {}, etc
 * @param value {any}
 * @returns {boolean}
 */
export const isExisty = (value) => {
  return value !== undefined && value !== null
}

/**
 * Check if session id exists
 * @param id {number} - session id
 * @returns {boolean}
 */
export const isValidId = (id = undefined) => {
  return id in store.getState().sessions.list
}

export const hasOutgoingRingingCall = () => {
  return store
    .getState()
    .sessions.list.filter(Boolean)
    .some((s) => s.outgoing && s.connecting)
}

/**
 * Helper to find a contact in a list of contacts
 * @param target - the contact to find
 * @param contacts - the list of contacts to search
 */
export const findContact = (target, mappedContacts = {}, flatContacts = []) => {
  const contacts = mappedContacts[target.type] || flatContacts
  const result = contacts.find((contact) => {
    return ['id', 'name', 'email', 'extension', 'phone'].some((key) => {
      const value = target?.[key]
      if (value && contact[key] === value) return true
      const { source } = target?.callDetails?.from || {}
      if (source && contact[key] === source) return true
      return false
    })
  })

  return { ...target, ...result }
}

/**
 * Gets the appropriate language code with country suffix based on country code. At this moment, only Canada (CA) is supported.
 *
 * @param {string} language - The base language code (e.g., 'en', 'fr')
 * @param {string} [countryCode] - The country code (e.g., 'CA', 'US')
 * @returns {string} The language code with country suffix if applicable (e.g., 'en-CA', 'fr-CA')
 */
export const getLanguageWithCountry = (language, countryCode) => {
  if (countryCode === 'CA') {
    if (language === 'en') return 'en-CA'
    if (language === 'fr') return 'fr-CA'
  }
  return language
}

/**
 * Gets the base language from a language code that may include a country suffix.
 *
 * @param {string} value - The language code (e.g., 'en', 'fr-CA')
 * @returns {string} The base language code (e.g., 'en', 'fr')
 */
export const getBaseLanguage = (value) => {
  const base = value.split('-')[0]
  return base
}
